import { InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL } from '@boss/constants/b2b-b2c';
import { IStore, content as contentService, events as eventService } from '@boss/services';
import { Crumble, Environment } from '@boss/types/b2b-b2c';
import { getLocalizedSlugForEventPage, getLocalizedSlugsForEventPage } from '@boss/utils';

import { ComponentMapper, SEO } from '../../components';
import { IS_BUILD, PageTypes } from '../../constants';
import EventsPage from '../../containers/eventsPage/eventsPage';
import TrainingsOverview from '../../containers/trainingsOverview/trainingsOverview';
import Layout from '../../layout/default';
import { eventType } from '../../utils';
import { resolveEventMarketContext } from '../../utils/eventMarket';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { getMappedNavigation } from '../../utils/navigation';
import { loadTrainings } from '../../utils/trainings/server';

export const getStaticProps = async ({ preview, locale }: { preview?: boolean; locale?: string }) => {
  const stores: IStore[] = [];

  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const { contentfulLocaleMarket, market } = resolveEventMarketContext(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  const [navigation, overviewPage, events] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    contentService.getOverviewPage({
      locale: contentfulLocaleMarket,
      channel: CHANNEL,
      type: 'Event overview',
      batchMode: IS_BUILD,
      preview: !!preview,
      market,
    }),
    // Live availability data: batch build data is stale by up to a day (45258).
    eventType === 'TRAINING' ? loadTrainings(locale as string) : eventService.getWorkshops(locale as string),
  ]);

  return {
    props: {
      pageType: PageTypes.EVENT_OVERVIEW,
      events,
      stores,
      locale,
      preview: !!preview,
      pageData: { overviewPage },
      navigation,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      initialFilters: null,
      ...(await getServerSideTranslations(locale, [
        'common',
        'stores',
        'event',
        'routes',
        'forms',
        'account',
        'workshop',
      ])),
    },
    revalidate: 3600,
  };
};

export const Index = ({
  navigation,
  locale,
  preview,
  pageData,
  environment,
  events,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const translatedSlugs = getLocalizedSlugsForEventPage();
  const { t } = useTranslation(['event', 'stores', 'account']);
  const pageTitle = t('pageTitle', { ns: 'event' }) ?? '';
  const pageDescription = pageTitle;

  const { overviewPage } = pageData;
  const hasPrefixSection = overviewPage?.prefixSections?.length;

  const crumbles: Crumble[] = [{ href: getLocalizedSlugForEventPage(locale), label: pageTitle }];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={overviewPage?.metaDescription ?? pageDescription}
      imageSrc={overviewPage?.ogImage?.image?.file?.url}
      localizedSlugs={translatedSlugs}
      navigation={navigation}
      noFollow={overviewPage?.noFollow}
      noIndex={overviewPage?.noIndex}
      title={overviewPage?.pageTitle ?? pageTitle}
    >
      <Layout
        breadcrumbClassName={hasPrefixSection ? 'mb-0 md:mb-0' : 'mb-4 md:mb-6'}
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={translatedSlugs}
        navigation={navigation}
        preview={preview}
      >
        {overviewPage?.prefixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
        {eventType === 'TRAINING' ? <TrainingsOverview events={events} /> : <EventsPage events={events} />}
        {overviewPage?.suffixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
      </Layout>
    </SEO>
  );
};

export default Index;
